import type { Dispatch, SetStateAction } from "react";
import { useEffect, useState } from "react";
import type { CalculateTipsResponse, TipDetails } from "model/Tip";
import {
  calculateTipDistribution,
  hasApprovedTimeReports,
  hasPersistedDistribution,
  mergeTips,
  saveTipAmount,
  saveTipDistribution,
  sortTipsByDate,
  toCalculationResultFromTips,
  toTipDistributionDrafts,
} from "../logic/tips.v2.logic";
import type {
  OpenCalculatedDistributionProps,
  RecalculateTeamDistributionProps,
  ResolveTipForModalProps,
  SaveHourDistributionProps,
  SaveTeamDistributionProps,
  TipDistributionActions,
  TipDistributionState,
  UndoDistributionProps,
  UseTipDistributionActionsProps,
} from "../tips.component.types";

const resolveTipForModal = async (
  props: ResolveTipForModalProps,
): Promise<TipDetails | null> => {
  if (props.selectedTip.amount === props.amount) return props.selectedTip;

  const result = await saveTipAmount({
    amount: props.amount,
    companyId: props.companyId,
    companyTipId: props.companyTipId,
  });

  if (!result.ok) {
    props.setErrorMessage(props.t("Tips.UnexpectedError"));
    return null;
  }

  props.setTips((currentTips) =>
    sortTipsByDate(
      currentTips.map((tip) =>
        tip.companyTipId === props.companyTipId ? result.value : tip,
      ),
    ),
  );
  return result.value;
};

const openCalculatedDistribution = async (
  props: OpenCalculatedDistributionProps,
): Promise<void> => {
  if (props.existingTips && props.existingTips.length > 0) {
    props.setDistributionTipIds([...props.companyTipIds]);
    props.setDistributionTip(props.selectedTip);
    props.setDistributionResult(
      toCalculationResultFromTips(props.existingTips),
    );
    return;
  }

  const result = await calculateTipDistribution({
    companyId: props.companyId,
    companyTipId: props.companyTipIds,
  });

  if (!result.ok) {
    props.setErrorMessage(props.t("Tips.UnexpectedError"));
    return;
  }

  props.setDistributionTipIds([...props.companyTipIds]);
  props.setDistributionTip(props.selectedTip);
  props.setDistributionResult(result.value);
};

const recalculateTeamDistribution = async (
  props: RecalculateTeamDistributionProps,
): Promise<void> => {
  const result = await calculateTipDistribution({
    companyId: props.companyId,
    companyTipId: props.companyTipIds,
    workGroups: props.workGroups,
  });

  if (result.ok) {
    props.setDistributionResult(result.value);
    return;
  }

  props.setErrorMessage(props.t("Tips.UnexpectedError"));
};

const saveHourDistribution = async (
  props: SaveHourDistributionProps,
): Promise<void> => {
  const result = await saveTipDistribution({
    companyId: props.companyId,
    drafts: props.drafts,
  });

  if (result.ok) {
    props.setTips((currentTips) =>
      mergeTips({ currentTips, nextTips: result.value }),
    );
    props.closeDistributionModal();
    return;
  }

  props.setErrorMessage(props.t("Tips.UnexpectedError"));
};

const saveTeamDistribution = async (
  props: SaveTeamDistributionProps,
): Promise<void> => {
  const result = await calculateTipDistribution({
    companyId: props.companyId,
    companyTipId: props.companyTipIds,
    workGroups: props.workGroups,
  });

  if (!result.ok) {
    props.setErrorMessage(props.t("Tips.UnexpectedError"));
    return;
  }

  props.setDistributionResult(result.value);
  const saveResult = await saveTipDistribution({
    companyId: props.companyId,
    drafts: toTipDistributionDrafts(result.value.companyTips),
  });

  if (saveResult.ok) {
    props.setTips((currentTips) =>
      mergeTips({ currentTips, nextTips: saveResult.value }),
    );
    props.closeDistributionModal();
    return;
  }

  props.setErrorMessage(props.t("Tips.UnexpectedError"));
};

const undoDistribution = async (
  props: UndoDistributionProps,
): Promise<void> => {
  const result = await saveTipDistribution({
    companyId: props.companyId,
    drafts: props.drafts,
  });

  if (result.ok) {
    props.setTips((currentTips) =>
      mergeTips({ currentTips, nextTips: result.value }),
    );
    props.closeDistributionModal();
    return;
  }

  props.setErrorMessage(props.t("Tips.UnexpectedError"));
};

export const useTipDistributionActions = (
  props: UseTipDistributionActionsProps,
): TipDistributionActions & TipDistributionState => {
  const [distributionTipIds, setDistributionTipIds] = useState<
    readonly number[]
  >([]);
  const [isSavingDistribution, setIsSavingDistribution] = useState(false);
  const [distributionTip, setDistributionTip] = useState<TipDetails | null>(
    null,
  );
  const [distributionResult, setDistributionResult] =
    useState<CalculateTipsResponse | null>(null);

  const closeDistributionModal = (): void => {
    setDistributionTipIds([]);
    setDistributionTip(null);
    setDistributionResult(null);
  };

  useEffect(() => {
    setDistributionTipIds([]);
    setDistributionTip(null);
    setDistributionResult(null);
    setIsSavingDistribution(false);
  }, [props.companyId]);

  const handleOpenDistributionModal = async (
    companyTipId: number,
    amount: number,
  ): Promise<void> => {
    if (props.companyId === null) return;

    const selectedTip = props.tips.find(
      (tip) => tip.companyTipId === companyTipId,
    );
    if (!selectedTip) return;
    if (!hasApprovedTimeReports(selectedTip)) {
      props.setErrorMessage(props.t("CompanyTip.NoApprovedReports"));
      return;
    }

    setIsSavingDistribution(true);
    props.setErrorMessage(null);

    try {
      const tipForModal = await resolveTipForModal({
        amount,
        companyId: props.companyId,
        companyTipId,
        selectedTip,
        setErrorMessage: props.setErrorMessage,
        setTips: props.setTips,
        t: props.t,
      });
      if (tipForModal === null) return;

      await openCalculatedDistribution({
        companyId: props.companyId,
        companyTipIds: [companyTipId],
        existingTips: hasPersistedDistribution(tipForModal)
          ? [tipForModal]
          : null,
        selectedTip: tipForModal,
        setDistributionResult,
        setDistributionTip,
        setDistributionTipIds,
        setErrorMessage: props.setErrorMessage,
        t: props.t,
      });
    } catch (_error: unknown) {
      props.setErrorMessage(props.t("Tips.UnexpectedError"));
    } finally {
      setIsSavingDistribution(false);
    }
  };

  const handleOpenBulkDistributionModal = async (
    companyTipIds: readonly number[],
  ): Promise<void> => {
    if (props.companyId === null || companyTipIds.length === 0) return;

    const selectedTip = props.tips.find((tip) =>
      companyTipIds.includes(tip.companyTipId),
    );
    if (!selectedTip) return;
    if (
      props.tips
        .filter((tip) => companyTipIds.includes(tip.companyTipId))
        .some((tip) => !hasApprovedTimeReports(tip))
    ) {
      props.setErrorMessage(props.t("CompanyTip.NoApprovedReports"));
      return;
    }

    setIsSavingDistribution(true);
    props.setErrorMessage(null);

    try {
      const selectedTips = props.tips.filter((tip) =>
        companyTipIds.includes(tip.companyTipId),
      );

      await openCalculatedDistribution({
        companyId: props.companyId,
        companyTipIds,
        existingTips:
          selectedTips.length > 0 &&
          selectedTips.every(hasPersistedDistribution)
            ? selectedTips
            : null,
        selectedTip,
        setDistributionResult,
        setDistributionTip,
        setDistributionTipIds,
        setErrorMessage: props.setErrorMessage,
        t: props.t,
      });
    } catch (_error: unknown) {
      props.setErrorMessage(props.t("Tips.UnexpectedError"));
    } finally {
      setIsSavingDistribution(false);
    }
  };

  const handleRecalculateTeamDistribution = async (
    workGroups: RecalculateTeamDistributionProps["workGroups"],
  ): Promise<void> => {
    if (props.companyId === null || distributionTipIds.length === 0) return;

    await recalculateTeamDistribution({
      companyId: props.companyId,
      companyTipIds: distributionTipIds,
      setDistributionResult: setDistributionResult as Dispatch<
        SetStateAction<CalculateTipsResponse | null>
      >,
      setErrorMessage: props.setErrorMessage,
      t: props.t,
      workGroups,
    });
  };

  const handleSaveHourDistribution = async (
    drafts: SaveHourDistributionProps["drafts"],
  ): Promise<void> => {
    if (props.companyId === null) return;

    setIsSavingDistribution(true);
    props.setErrorMessage(null);

    try {
      await saveHourDistribution({
        closeDistributionModal,
        companyId: props.companyId,
        drafts,
        setErrorMessage: props.setErrorMessage,
        setTips: props.setTips,
        t: props.t,
      });
    } catch (_error: unknown) {
      props.setErrorMessage(props.t("Tips.UnexpectedError"));
    } finally {
      setIsSavingDistribution(false);
    }
  };

  const handleSaveTeamDistribution = async (
    workGroups: SaveTeamDistributionProps["workGroups"],
  ): Promise<void> => {
    if (props.companyId === null || distributionTipIds.length === 0) return;

    setIsSavingDistribution(true);
    props.setErrorMessage(null);

    try {
      await saveTeamDistribution({
        closeDistributionModal,
        companyId: props.companyId,
        companyTipIds: distributionTipIds,
        setDistributionResult,
        setErrorMessage: props.setErrorMessage,
        setTips: props.setTips,
        t: props.t,
        workGroups,
      });
    } catch (_error: unknown) {
      props.setErrorMessage(props.t("Tips.UnexpectedError"));
    } finally {
      setIsSavingDistribution(false);
    }
  };

  const handleUndoDistribution = async (
    drafts: UndoDistributionProps["drafts"],
  ): Promise<void> => {
    if (props.companyId === null) return;

    setIsSavingDistribution(true);
    props.setErrorMessage(null);

    try {
      await undoDistribution({
        closeDistributionModal,
        companyId: props.companyId,
        drafts,
        setErrorMessage: props.setErrorMessage,
        setTips: props.setTips,
        t: props.t,
      });
    } finally {
      setIsSavingDistribution(false);
    }
  };

  return {
    closeDistributionModal,
    distributionResult,
    distributionTip,
    handleOpenBulkDistributionModal,
    handleOpenDistributionModal,
    handleRecalculateTeamDistribution,
    handleSaveHourDistribution,
    handleSaveTeamDistribution,
    handleUndoDistribution,
    isSavingDistribution,
  };
};
